// TODO: vitest component test (sibling PublicCheckInStickerCard tested in
// web/src/test/parking/zones/PublicCheckInStickerCard.test.tsx)
import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, Button, Input, Alert } from '@/components/ui'
import { Trash2, Plus, Loader2 } from 'lucide-react'
import { useUpdateSessionFee } from '@/hooks/useParkingZones'
import type { ParkingFeeLine } from '@/types/parking'

interface Props {
  zoneId: number
  /** Existing formula (when present). Lines that aren't `hourly` are filtered out — the form is tier-only. */
  initialFormula: ParkingFeeLine[] | null | undefined
}

interface TierDraft {
  max_hours: string
  amount: string
}

/** Default seed for a fresh zone (operator only sees this on first edit). */
const DEFAULT_TIERS: TierDraft[] = [
  { max_hours: '6',  amount: '50'  },
  { max_hours: '12', amount: '100' },
  { max_hours: '24', amount: '150' },
]

const MAX_TIERS = 6

/**
 * Operator tier-pricing editor (EP-105).
 *
 * Renders only when the parent has admin access (the backend serializes the
 * formula only for admins; non-admins never see this section). Edits the
 * kind:'hourly' tier lines of the zone's session_fee_formula.
 *
 * Validation fires on Save; the backend re-validates and returns typed codes
 * (`tiers_unsorted` / `duplicate_tier_max_hours` / `tier_max_hours_out_of_range`
 * / `validation_failed`) which the toast surfaces verbatim.
 */
export function ParkingFeeTiersSection({ zoneId, initialFormula }: Props) {
  const { t } = useTranslation('parking')
  const { t: tc } = useTranslation('common')
  const updateFee = useUpdateSessionFee()

  // Seed from existing hourly tiers, fall back to the canonical defaults
  // (≤6h ฿50 / ≤12h ฿100 / ≤24h ฿150) on a brand-new zone.
  const seedFromExisting = useCallback((): TierDraft[] => {
    const existing = (initialFormula ?? [])
      .filter((l): l is Extract<ParkingFeeLine, { kind: 'hourly' }> => l.kind === 'hourly')
      .sort((a, b) => a.max_hours - b.max_hours)
    if (existing.length > 0) {
      return existing.map((l) => ({ max_hours: String(l.max_hours), amount: String(l.amount) }))
    }
    return DEFAULT_TIERS
  }, [initialFormula])

  const [tiers, setTiers] = useState<TierDraft[]>(seedFromExisting)
  const [error, setError] = useState<string | null>(null)

  // Re-seed when an external query refresh changes the underlying formula.
  useEffect(() => {
    setTiers(seedFromExisting())
  }, [seedFromExisting])

  const updateTierAt = (idx: number, patch: Partial<TierDraft>) => {
    setTiers((prev) => prev.map((row, i) => (i === idx ? { ...row, ...patch } : row)))
    setError(null)
  }

  const addTier = () => {
    if (tiers.length >= MAX_TIERS) return
    // Pick a max_hours above the last tier (so it's still ascending on the
    // operator's side without re-sorting; the backend validates the order).
    const lastHours = Number(tiers[tiers.length - 1]?.max_hours ?? 0)
    setTiers((prev) => [...prev, { max_hours: String(Math.min(lastHours + 6, 24)), amount: '0' }])
  }

  const removeTierAt = (idx: number) => {
    if (tiers.length <= 1) return
    setTiers((prev) => prev.filter((_, i) => i !== idx))
    setError(null)
  }

  const validateLocally = (rows: TierDraft[]): string | null => {
    const values = rows.map((r) => ({
      hours: Number(r.max_hours),
      amount: Number(r.amount),
    }))
    if (values.some((v) => !Number.isFinite(v.hours) || v.hours <= 0 || v.hours > 24)) {
      return t('zones.public_checkin.tier_validation_hours_range', { ns: 'parking' })
    }
    if (values.some((v) => !Number.isFinite(v.amount) || v.amount < 0)) {
      return t('zones.public_checkin.tier_validation_amount_positive', { ns: 'parking' })
    }
    const sorted = [...values.map((v) => v.hours)].sort((a, b) => a - b)
    if (sorted.some((h, i) => h !== values[i].hours)) {
      return t('zones.public_checkin.tier_validation_sorted', { ns: 'parking' })
    }
    if (new Set(values.map((v) => v.hours)).size !== values.length) {
      return t('zones.public_checkin.tier_validation_unique', { ns: 'parking' })
    }
    return null
  }

  const onSave = async () => {
    setError(null)
    const localErr = validateLocally(tiers)
    if (localErr) {
      setError(localErr)
      return
    }
    const formula: ParkingFeeLine[] = tiers.map((r) => ({
      kind: 'hourly',
      max_hours: Number(r.max_hours),
      amount: Number(r.amount),
    }))
    try {
      await updateFee.mutateAsync({ id: zoneId, formula })
    } catch (e: unknown) {
      const data = (e as { response?: { data?: { error?: { code?: string; message?: string } } } })?.response?.data
      const code = data?.error?.code ?? 'unknown'
      const message = data?.error?.message ?? ''
      // Be lenient with Thai / English: backend message wins if present.
      setError(message || `[${code}]`)
    }
  }

  return (
    <Card>
      <div className="space-y-4">
        <div>
          <h3 className="text-base font-semibold">
            {t('zones.public_checkin.tiers_title', { ns: 'parking' })}
          </h3>
          <p className="text-sm text-base-content/70 mt-1">
            {t('zones.public_checkin.tiers_description', { ns: 'parking' })}
          </p>
        </div>

        <div className="space-y-3">
          <div className="hidden sm:grid grid-cols-[1fr_1fr_auto] gap-3 text-xs font-medium uppercase text-base-content/60 px-1">
            <span>{t('zones.public_checkin.tiers_col_hours', { ns: 'parking' })}</span>
            <span>{t('zones.public_checkin.tiers_col_amount', { ns: 'parking' })}</span>
            <span className="w-10" />
          </div>

          {tiers.map((row, idx) => (
            <div
              key={`tier-${idx}`}
              className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end"
            >
              <Input
                type="number"
                min={1}
                max={24}
                step={1}
                value={row.max_hours}
                onChange={(e) => updateTierAt(idx, { max_hours: e.target.value })}
                aria-label={t('zones.public_checkin.tiers_col_hours', { ns: 'parking' })}
              />
              <Input
                type="number"
                min={0}
                step={1}
                value={row.amount}
                onChange={(e) => updateTierAt(idx, { amount: e.target.value })}
                aria-label={t('zones.public_checkin.tiers_col_amount', { ns: 'parking' })}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={tiers.length <= 1}
                onClick={() => removeTierAt(idx)}
                aria-label={t('zones.public_checkin.tiers_remove', { ns: 'parking' })}
                leftIcon={<Trash2 className="h-4 w-4" />}
              >
                <span className="sr-only">
                  {t('zones.public_checkin.tiers_remove', { ns: 'parking' })}
                </span>
              </Button>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={tiers.length >= MAX_TIERS}
            onClick={addTier}
            leftIcon={<Plus className="h-4 w-4" />}
          >
            {t('zones.public_checkin.tiers_add', { ns: 'parking' })}
          </Button>

          <span className="text-xs text-base-content/60">
            {t('zones.public_checkin.tiers_count', {
              ns: 'parking',
              count: tiers.length,
            })}
          </span>
        </div>

        {error && (
          <Alert role="alert" variant="error" className="text-sm">
            {error}
          </Alert>
        )}

        <div className="flex justify-end">
          <Button
            type="button"
            onClick={() => {
              void onSave()
            }}
            disabled={updateFee.isPending}
            leftIcon={updateFee.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : undefined}
          >
            {updateFee.isPending ? (
              <span>{tc('actions.saving')}</span>
            ) : (
              <span>{tc('actions.save')}</span>
            )}
          </Button>
        </div>
      </div>
    </Card>
  )
}
